import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { OKLAHOMA_CENTER, type BBox, type LngLat, type ScaleWindow } from '@okmaps/shared';

/**
 * An extent to frame, and the scale range the thing being framed can draw at.
 *
 * The scale travels with the extent because framing one without the other is
 * how the map ends up correctly positioned over a layer that renders nothing:
 * 227 of the published layers only draw inside a scale window, and most of
 * those windows exclude the statewide view their own bounding box produces.
 */
export interface PendingExtent extends ScaleWindow {
  readonly bbox: BBox;
}

export type BasemapId = 'osm' | 'topo' | 'none';

/**
 * Desired map view state.
 *
 * This is the view the application *wants*; the SDK owns the view it currently
 * has. `esriSync` pushes changes down and echoes user-driven pan/zoom back via
 * `viewMoved`, which is marked so the sync layer can ignore its own echo and
 * avoid a feedback loop.
 */
export interface MapState {
  readonly center: LngLat;
  readonly zoom: number;
  readonly basemap: BasemapId;
  /** Set when something asks the map to frame an extent. Cleared once applied. */
  readonly pendingExtent: PendingExtent | null;
  readonly ready: boolean;
  /**
   * Where the user last clicked to identify features, with the map resolution
   * at that moment. The resolution travels with the point because the pick
   * tolerance has to be a distance on the ground, not a fixed number of
   * degrees: six pixels is a few meters when zoomed in and several kilometers
   * when zoomed out.
   */
  readonly identifyAt: { point: LngLat; resolution: number } | null;
}

const initialState: MapState = {
  center: OKLAHOMA_CENTER,
  zoom: 7,
  basemap: 'osm',
  pendingExtent: null,
  ready: false,
  identifyAt: null,
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    /** The SDK has loaded and the view is usable. */
    mapReady(state) {
      state.ready = true;
    },
    /** Echo of a user-driven pan or zoom. Does not trigger a sync back down. */
    viewMoved(state, action: PayloadAction<{ center: LngLat; zoom: number }>) {
      state.center = action.payload.center;
      state.zoom = action.payload.zoom;
    },
    basemapChanged(state, action: PayloadAction<BasemapId>) {
      state.basemap = action.payload;
    },
    extentRequested(state, action: PayloadAction<PendingExtent>) {
      // The bbox is copied element-wise: Immer's draft type strips `readonly`
      // from the tuple, so the readonly BBox cannot be assigned to it directly.
      const { bbox, minScale, maxScale } = action.payload;
      const [west, south, east, north] = bbox;
      state.pendingExtent = {
        bbox: [west, south, east, north],
        ...(minScale === undefined ? {} : { minScale }),
        ...(maxScale === undefined ? {} : { maxScale }),
      };
    },
    /** Called by the sync layer once it has applied `pendingExtent`. */
    extentApplied(state) {
      state.pendingExtent = null;
    },
    identifyRequested(
      state,
      action: PayloadAction<{ point: LngLat; resolution: number }>,
    ) {
      state.identifyAt = action.payload;
    },
    identifyDismissed(state) {
      state.identifyAt = null;
    },
    flyToRequested(state, action: PayloadAction<{ center: LngLat; zoom?: number }>) {
      state.center = action.payload.center;
      if (action.payload.zoom !== undefined) state.zoom = action.payload.zoom;
    },
  },
});

export const {
  mapReady,
  viewMoved,
  basemapChanged,
  extentRequested,
  extentApplied,
  identifyRequested,
  identifyDismissed,
  flyToRequested,
} = mapSlice.actions;

export default mapSlice.reducer;
